/**
 * InvoiceReminderPortalPage — Mahnungs-Antwort-Portal (Jun-2026 User-Wunsch).
 *
 * Öffentliche, tokenbasierte Seite (kein Login). Kunde erreicht sie über den
 * Link in der Mahnungs-Mail: /zahlung/<token>. Funktionen:
 *  - Rechnungs-/Mahn-Info ansehen
 *  - Rückmeldung erfassen (Nachricht + „bereits bezahlt")
 *  - Belege hochladen (z.B. Überweisungsnachweis)
 */
import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import DealerBrandLogo from "../components/brand/DealerBrandLogo";
import PublicLegalConsent from "../components/public/PublicLegalConsent";
import { CheckCircle2, Upload, Loader2, FileText, AlertCircle, Send, CreditCard } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEur = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" }).format(Number(v || 0));

export default function InvoiceReminderPortalPage() {
    const { token } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [message, setMessage] = useState("");
    const [claimsPaid, setClaimsPaid] = useState(false);
    const [sending, setSending] = useState(false);
    const [sent, setSent] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [paying, setPaying] = useState(false);
    const [payMsg, setPayMsg] = useState(null);
    const [legalOk, setLegalOk] = useState(false);
    const fileRef = useRef(null);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const r = await axios.get(`${API}/public/invoice/${token}`);
            setData(r.data);
            if (r.data?.customer_response?.message) setMessage(r.data.customer_response.message);
            if (r.data?.customer_response?.claims_paid) setClaimsPaid(true);
        } catch (e) {
            setError(e.response?.data?.detail || "Rechnung konnte nicht geladen werden");
        } finally { setLoading(false); }
    }, [token]);

    useEffect(() => { load(); }, [load]);

    // Rückkehr von Stripe: session_id in der URL → Zahlungsstatus pollen.
    const pollStatus = useCallback(async (sid, attempt = 0) => {
        if (attempt >= 6) { setPayMsg({ type: "pending", text: "Zahlung wird noch verarbeitet. Bitte später erneut prüfen." }); return; }
        try {
            const r = await axios.get(`${API}/public/invoice/payment-status/${sid}`);
            if (r.data?.payment_status === "paid") {
                setPayMsg({ type: "success", text: "Zahlung erfolgreich – vielen Dank!" });
                await load();
                return;
            }
            if (r.data?.status === "expired") { setPayMsg({ type: "error", text: "Zahlungssitzung abgelaufen. Bitte erneut versuchen." }); return; }
            setPayMsg({ type: "pending", text: "Zahlung wird verarbeitet…" });
            setTimeout(() => pollStatus(sid, attempt + 1), 2000);
        } catch {
            setPayMsg({ type: "error", text: "Zahlungsstatus konnte nicht geprüft werden." });
        }
    }, [load]);

    useEffect(() => {
        const sid = new URLSearchParams(window.location.search).get("session_id");
        if (sid) { setPayMsg({ type: "pending", text: "Zahlungsstatus wird geprüft…" }); pollStatus(sid); }
    }, [pollStatus]);

    const payOnline = async () => {
        setPaying(true);
        try {
            const r = await axios.post(`${API}/public/invoice/${token}/pay`, { origin_url: window.location.origin });
            if (r.data?.url) window.location.href = r.data.url;
            else throw new Error("Keine Checkout-URL erhalten");
        } catch (e) {
            setError(e.response?.data?.detail || "Online-Zahlung konnte nicht gestartet werden");
            setPaying(false);
        }
    };

    const submit = async () => {
        setSending(true);
        try {
            await axios.post(`${API}/public/invoice/${token}/respond`, { message, claims_paid: claimsPaid });
            setSent(true);
            await load();
        } catch (e) {
            setError(e.response?.data?.detail || "Senden fehlgeschlagen");
        } finally { setSending(false); }
    };

    const upload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            await axios.post(`${API}/public/invoice/${token}/upload`, fd);
            await load();
        } catch (err) {
            setError(err.response?.data?.detail || "Upload fehlgeschlagen");
        } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
    };

    if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-500"><Loader2 className="w-5 h-5 animate-spin mr-2" /> Lädt…</div>;
    if (error && !data) return (
        <div className="min-h-screen flex items-center justify-center p-6">
            <div className="bg-white border border-red-200 rounded-sm p-6 max-w-md text-center" data-testid="invoice-portal-error">
                <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-gray-700">{error}</p>
            </div>
        </div>
    );

    const uploads = data?.customer_response?.uploads || [];

    return (
        <div className="min-h-screen bg-gray-50 py-10 px-4" data-testid="invoice-reminder-portal">
            <div className="max-w-lg mx-auto">
                <div className="bg-[#111827] text-white rounded-t-sm px-6 py-5">
                    <div className="mb-1">
                        <DealerBrandLogo variant="plain" logoUrl={data?.dealer_logo} companyName={data?.dealer_name}
                            imgClassName="h-8 max-w-[180px]" textClassName="text-[11px] uppercase tracking-wider text-[#FFCC00] font-bold" />
                    </div>
                    <h1 className="text-xl font-black mt-1">Rechnung {data?.invoice_number}</h1>
                </div>
                <div className="bg-white border border-t-0 border-gray-200 rounded-b-sm p-6 space-y-5">
                    {data?.already_paid ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-emerald-800 flex items-center gap-2" data-testid="invoice-portal-paid">
                            <CheckCircle2 className="w-5 h-5" /> Diese Rechnung ist ausgeglichen. Vielen Dank!
                        </div>
                    ) : (
                        <div className="grid grid-cols-2 gap-3">
                            <div className="bg-gray-50 rounded-sm p-3">
                                <div className="text-2xl font-black font-mono-num text-[#111827]">{fmtEur(data?.open_amount)}</div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mt-1">Offener Betrag</div>
                            </div>
                            <div className="bg-gray-50 rounded-sm p-3">
                                <div className="text-2xl font-black font-mono-num text-[#111827]">{data?.due_date || "—"}</div>
                                <div className="text-[10px] uppercase tracking-wider text-gray-500 font-bold mt-1">Fällig bis</div>
                            </div>
                        </div>
                    )}

                    {payMsg && (
                        <div className={`rounded-sm p-3 text-sm flex items-center gap-2 ${payMsg.type === "success" ? "bg-emerald-50 border border-emerald-200 text-emerald-800" : payMsg.type === "error" ? "bg-red-50 border border-red-200 text-red-700" : "bg-amber-50 border border-amber-200 text-amber-800"}`} data-testid="invoice-portal-pay-status">
                            {payMsg.type === "success" ? <CheckCircle2 className="w-4 h-4" /> : payMsg.type === "error" ? <AlertCircle className="w-4 h-4" /> : <Loader2 className="w-4 h-4 animate-spin" />} {payMsg.text}
                        </div>
                    )}

                    {!sent && (
                        <PublicLegalConsent token={token} portal="invoice_reminder" onAcceptedChange={setLegalOk} accent="#059669" />
                    )}

                    {!data?.already_paid && (
                        <button type="button" onClick={payOnline} disabled={paying || !legalOk}
                            className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 text-white font-bold rounded-sm py-3 hover:bg-emerald-700 disabled:opacity-60"
                            data-testid="invoice-portal-pay-online">
                            {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />} Jetzt online bezahlen ({fmtEur(data?.open_amount)})
                        </button>
                    )}

                    {sent ? (
                        <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-4 text-emerald-800 flex items-center gap-2" data-testid="invoice-portal-success">
                            <CheckCircle2 className="w-5 h-5" /> Vielen Dank — Ihre Rückmeldung ist bei uns eingegangen.
                        </div>
                    ) : (
                        <>
                            <label className="flex items-start gap-2 cursor-pointer" data-testid="invoice-portal-claims-paid">
                                <input type="checkbox" checked={claimsPaid} onChange={(e) => setClaimsPaid(e.target.checked)} className="mt-1" />
                                <span className="text-sm text-gray-700">Ich habe bereits bezahlt (Beleg lade ich unten hoch)</span>
                            </label>
                            <div>
                                <label className="text-xs font-bold uppercase tracking-wider text-gray-500">Ihre Nachricht (optional)</label>
                                <textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3}
                                    placeholder="z.B. Zahlung erfolgt am … / Rückfrage zur Rechnung"
                                    className="mt-1 w-full border border-gray-300 rounded-sm p-2 text-sm" data-testid="invoice-portal-message" />
                            </div>
                            <button type="button" onClick={submit} disabled={sending || !legalOk}
                                className="w-full inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-[#111827] font-bold rounded-sm py-2.5 hover:brightness-95 disabled:opacity-60"
                                data-testid="invoice-portal-submit">
                                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Rückmeldung senden
                            </button>
                        </>
                    )}

                    {/* Beleg-Upload */}
                    <div className="border-t border-gray-200 pt-4">
                        <div className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-2">Belege hochladen</div>
                        <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={upload} className="hidden" data-testid="invoice-portal-file-input" />
                        <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                            className="inline-flex items-center gap-2 border border-gray-300 rounded-sm px-4 py-2 text-sm font-bold text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                            data-testid="invoice-portal-upload-btn">
                            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Datei auswählen (PDF/Bild)
                        </button>
                        {uploads.length > 0 && (
                            <ul className="mt-3 space-y-1" data-testid="invoice-portal-uploads">
                                {uploads.map((u, i) => (
                                    <li key={i} className="text-xs text-gray-600 flex items-center gap-2">
                                        <FileText className="w-3.5 h-3.5 text-emerald-600" /> {u.name}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                    {error && <p className="text-xs text-red-600">{error}</p>}
                </div>
                <p className="text-center text-[11px] text-gray-400 mt-4">Sichere Verbindung · {data?.dealer_name}</p>
            </div>
        </div>
    );
}
